import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from './input';
import { MapPin, Loader2 } from 'lucide-react';

/**
 * AddressAutocomplete Component
 * Uses Google Places API for address autocomplete
 * 
 * Usage:
 * <AddressAutocomplete
 *   value={address.street}
 *   onAddressSelect={(addressData) => setAddress(addressData)}
 *   placeholder="Start typing address..."
 *   className="..."
 * />
 * 
 * addressData returned: { street, city, postal_code, country, formatted_address, lat, lng }
 */

const AddressAutocomplete = ({ 
  value, 
  onChange,
  onAddressSelect, 
  placeholder = "Start typing address...",
  className = "",
  disabled = false,
  "data-testid": testId = "address-autocomplete"
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');

  const parseAddressComponents = useCallback((place) => {
    const components = place.address_components || [];
    
    let streetNumber = '';
    let route = '';
    let city = '';
    let postalCode = '';
    let country = 'UK';
    let county = '';

    components.forEach(component => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('locality') || types.includes('postal_town')) {
        city = component.long_name;
      }
      if (types.includes('postal_code')) {
        postalCode = component.long_name;
      }
      if (types.includes('country')) {
        country = component.short_name;
      }
      if (types.includes('administrative_area_level_2')) {
        county = component.long_name;
      }
    });

    // Build street address
    const street = streetNumber && route 
      ? `${streetNumber} ${route}` 
      : route || place.formatted_address?.split(',')[0] || '';

    return {
      street,
      city: city || county,
      postal_code: postalCode,
      country,
      formatted_address: place.formatted_address,
      lat: place.geometry?.location?.lat(),
      lng: place.geometry?.location?.lng()
    };
  }, []);

  const initAutocomplete = useCallback(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;

    // Initialize Google Places Autocomplete
    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: ['gb', 'ie'] }, // UK and Ireland
      fields: ['address_components', 'formatted_address', 'geometry']
    });

    // Handle place selection
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      
      // Handle case where user pressed enter without selecting from dropdown
      if (!place || !place.address_components) {
        // Just use the input value as-is
        return;
      }

      // Parse address components
      const addressData = parseAddressComponents(place);
      
      setInputValue(addressData.street || place.formatted_address);
      
      if (onAddressSelect) {
        onAddressSelect(addressData);
      }
      
      if (onChange) {
        onChange({ target: { value: addressData.street || place.formatted_address } });
      }
    });
  }, [onAddressSelect, onChange, parseAddressComponents]);

  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsLoaded(true);
      initAutocomplete();
      return;
    }

    // Load Google Maps script if not already loaded
    const existingScript = document.getElementById('google-maps-script');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsLoaded(true);
        initAutocomplete();
      };
      document.head.appendChild(script);
    } else {
      // Script exists, wait for it to load
      existingScript.addEventListener('load', () => {
        setIsLoaded(true);
        initAutocomplete();
      });
      // If already loaded
      if (window.google && window.google.maps) {
        setIsLoaded(true);
        initAutocomplete();
      }
    }

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [initAutocomplete]);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className="relative">
      <MapPin 
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" 
        size={16} 
      />
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={`pl-9 ${className}`}
        disabled={disabled}
        data-testid={testId}
        autoComplete="off"
      />
      {!isLoaded && (
        <Loader2 
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" 
          size={16} 
        />
      )}
    </div>
  );
};

export default AddressAutocomplete;
